import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  AfterViewInit,
  OnDestroy,
  SimpleChanges,
  ViewChild,
  TemplateRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { PageEvent, MatPaginatorModule } from '@angular/material/paginator';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatMenuModule } from '@angular/material/menu';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatBadgeModule } from '@angular/material/badge';
import { MatSelectModule } from '@angular/material/select';
import { MatSortModule } from '@angular/material/sort';
import { SelectionModel } from '@angular/cdk/collections';
import { Subject, lastValueFrom } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FuseNavigationService } from '@fuse/components/navigation/navigation.service';
import { FuseVerticalNavigationComponent } from '@fuse/components/navigation/vertical/vertical.component';
import { NgSelectModule } from '@ng-select/ng-select';

// AVE Models
import {
  OrderAttributes,
  OrderDetailAttributes,
  CompanyAttributes,
  DocumentClassAttributes,
  WarehouseAttributes,
  OrderExtraStatusAttributes,
  MaterialAttributes,
  MaterialStockAttributes,
  OrderDetailUnitOfMeasureAttributes,
} from '@albatrosdeveloper/ave-models-package';
import OrderPaymentAttributes from '@albatrosdeveloper/ave-models-package/lib/schemas/process/order-payment/order-payment.entity';
import SalesOfficeAttributes from '@albatrosdeveloper/ave-models-package/lib/schemas/configurations/sales-office/sales-office.entity';
import { OrderStatusERPEnum } from '@albatrosdeveloper/ave-models-package/lib/enums/order-status-erp.enum';
import { ActionRequirementEnum } from '@albatrosdeveloper/ave-models-package/lib/schemas/documents/document-class/enums/action-requirement.enum';

// Services
import { OrdersListService } from '../../sales/order-list-components/order-list.service';
import { UpdateOrderService } from '../../sales/update-order/update-order.service';
import { OrdersCreationService } from '../../sales/order-generate/order-generate.service';
import { SalesOfficeService } from '../../configuration/sales-office/sales-office.service';
import { WarehouseService } from '../../configuration/warehouse/warehouse.service';
import { DocumentClassService } from '../../document/document-class/document-class.service';
import { CompanyService } from '../../configuration/company/company.service.service';
import { OrderPaymentAService } from '../../reports/historic/order-payment/order-payment.service';
import { LoadsService } from '../../assistant/delivery/loads/loads.service';
import { InvoiceService } from '../../sales/invoice-list/Print/pdf-generator.service';
import { ExportService } from 'app/layout/export-overlay/export-overlay.service';

// Utilities and Pipes
import { getCode } from 'app/utils/method';
import { environment } from 'environments/environment';
import { DecimalFormatPipe } from 'app/pipes/decimal-company.pipe';
import { validateOrdersByAction } from 'app/utils/document-class-validation';

// Shared Components
import { BusinessPartnerDisplayComponent } from 'app/shared/components/business-partner-display/business-partner-display.component';
import { DocumentNavigationComponent } from 'app/common/components/document-navigation/document-navigation.component';
import { DocumentFlowButtonComponent } from 'app/modules/shared/document-flow-button/document-flow-button.component';
import { NoteDetailModalComponent } from 'app/modules/shared/note-detail-modal/note-detail-modal.component';
import { BatchesDetailComponent } from 'app/modules/shared/batches-detail/batches-detail.component';
import { CreditNoteDialogComponent } from '../payment-load-list/credit-note/credit-note.component';
import { ChangeTripOrderComponent } from '../../sales/change-trip-order/change-trip-order.component';
import { LogModalComponent } from 'app/modules/shared/log-modal/log-modal.component';
import { CustomDialogComponent } from 'app/shared/components/custom-dialog/custom-dialog.component';
import { ManualLabelSelectionIndicatorComponent } from 'app/shared/components/manual-label-selection-indicator';
import { InvoicePdfOptions } from '../../sales/invoice-list/Print/invoice-pdf-options.interface';
import { IOrderDetailsUpdate } from './interfaces/orderDetailsUpdate.interface';
import { IUpdateOrder } from '../../sales/update-order/interfaces/update-order.interface';

// Smart Grid
import { SmartGridComponent, ColDef, GridConfig } from 'app/shared/components/smart-grid';
import { DecimalCellRendererComponent, DateTimeCellRendererComponent } from 'app/shared/components/smart-grid';

// Cell Renderers
import { OrderStatusCellRendererComponent } from './cell-renderers/order-status-cell-renderer/order-status-cell-renderer.component';
import { OrderERPStatusCellRendererComponent } from './cell-renderers/order-erp-status-cell-renderer/order-erp-status-cell-renderer.component';
import { DocumentTypeCellRendererComponent } from './cell-renderers/document-type-cell-renderer/document-type-cell-renderer.component';
import { DocumentNumberCellRendererComponent } from './cell-renderers/document-number-cell-renderer/document-number-cell-renderer.component';
import { DocumentClassCellRendererComponent } from './cell-renderers/document-class-cell-renderer/document-class-cell-renderer.component';
import { LoadDeliveryDateCellRendererComponent } from './cell-renderers/load-delivery-date-cell-renderer/load-delivery-date-cell-renderer.component';
import { OrderActionsCellRendererComponent } from './cell-renderers/order-actions-cell-renderer/order-actions-cell-renderer.component';
import { OrderStatusIconsCellRendererComponent } from './cell-renderers/order-status-icons-cell-renderer/order-status-icons-cell-renderer.component';
import { BusinessPartnerCellRendererComponent } from './cell-renderers/business-partner-cell-renderer/business-partner-cell-renderer.component';
import { GlobalActionsCellRendererComponent } from './cell-renderers/global-actions-cell-renderer/global-actions-cell-renderer.component';
import { MonedaPipe } from 'app/pipes/moneda.pipe';
import { IUpdateOrderUnified } from '../../sales/update-order/interfaces/update-order.interface';
import { BatchesDetailDialogService } from "app/modules/shared/batches-detail/batches-detail-dialog.service";

/**
 * Smart Grid Implementation of Table Load List Component
 *
 * This component replaces the original table-load-list.component with a smart-grid-based
 * implementation while maintaining 100% feature parity.
 *
 * Reference: src/app/modules/components/monitor/table-load-list/table-load-list.component.ts
 * PRD: docs/prd-generation/tasks/0001-prd-migrate-pedidos-table-to-smart-grid.md
 */
@Component({
  selector: "app-table-load-list-smart-grid",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    MatPaginatorModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatTooltipModule,
    MatMenuModule,
    MatTabsModule,
    MatTableModule,
    MatCheckboxModule,
    MatBadgeModule,
    MatSelectModule,
    MatSortModule,
    NgSelectModule,
    DecimalFormatPipe,
    SmartGridComponent,
    MonedaPipe,
    ManualLabelSelectionIndicatorComponent,
  ],
  templateUrl: "./table-load-list-smart-grid.component.html",
  styleUrls: ["./table-load-list-smart-grid.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TableLoadListSmartGridComponent
  implements OnInit, OnChanges, AfterViewInit, OnDestroy
{
  private readonly batchesDialog = inject(BatchesDetailDialogService);

  /** El gate mira el arreglo que el modal va a abrir, no siempre el de cargados. */
  hasBatches(detail: any): boolean {
    return this.batchesDialog.hasBatches(this.selectedOrder?.type?.code, detail);
  }
  // Navigation state for conditional styling
  isNavOpened: boolean = true;

  // ============================================================================
  // INPUTS (copied from original table-load-list.component.ts)
  // ============================================================================

  /** The order data to display in the grid */
  @Input() summaryData: OrderAttributes[] | null = [];

  /** Total count of items for pagination */
  @Input() totalItems: number = 0;

  /** Items per page */
  @Input() pageSize: number = 50;

  /** Current page number (1-based) */
  @Input() currentPage: number = 1;

  /** Unit of measure parallel flag */
  @Input() UMPActive: boolean = false;

  /** Show all columns flag */
  @Input() isAllColumns: boolean = true;

  /** Show merma columns configuration */
  @Input() isAllColumnsMerma: boolean = false;

  /** Show transport columns configuration */
  @Input() isAllColumnsTransport: boolean = false;

  /** Show search input flag */
  @Input() isSearch: boolean = false;

  /** Invoice mode flag */
  @Input() isFact: boolean = false;

  /** Can unify orders flag */
  @Input() canJoin: boolean = false;

  /** Can edit orders flag */
  @Input() canEdit: boolean = false;

  /** Can release orders flag */
  @Input() canRelease: boolean = false;

  /** Related payment data */
  @Input() relatedPayments: any[] = [];

  /** Show batches detail flag */
  @Input() batchesDetail: boolean = false;

  // ============================================================================
  // OUTPUTS (copied from original table-load-list.component.ts)
  // ============================================================================

  /** Emitted when pagination changes (page index or page size) */
  @Output() pageChanged = new EventEmitter<PageEvent>();

  /** Emitted when the parent should refresh/reload monitor data */
  @Output() refreshMonitor = new EventEmitter<void>();

  // ============================================================================
  // PROPERTIES (from original component)
  // ============================================================================

  /** Helper function to extract code from objects */
  getCode = getCode;

  /** Project name from environment */
  projectName = environment.projectName;

  /** Order status ERP enum */
  OrderStatusERPEnum = OrderStatusERPEnum;

  /** Selected order for expansion */
  selectedOrder: OrderAttributes | null = null;

  /** Selection model for checkboxes */
  selection = new SelectionModel<OrderAttributes>(true, []);

  /** Flag to indicate if global synchronize is available (all selected items can be synchronized) */
  isGlobalSynchronizeAvailable: boolean = false;

  /** Data source for order details (in expanded row) */
  dataSourceDetail = new MatTableDataSource<OrderDetailAttributes>([]);

  /** Data source for payment methods (in expanded row) */
  paymentMethodsDataSource = new MatTableDataSource<OrderPaymentAttributes>([]);

  /** Map to store detail data for each expanded row */
  private expandedRowDetails = new Map<string, OrderDetailAttributes[]>();

  /** Map to store payment data for each expanded row */
  private expandedRowPayments = new Map<string, OrderPaymentAttributes[]>();

  /** Destroy subject for cleanup */
  private destroy$ = new Subject<void>();

  /** Reference to the row details template */
  @ViewChild("rowDetailsTemplate", { static: false })
  rowDetailsTemplate!: TemplateRef<any>;

  /** Reference to the confirm dialog template */
  @ViewChild("confirmDialog", { static: false })
  confirmDialog!: TemplateRef<any>;

  /** Reference to the edit modal template */

  /** Reference to the SmartGrid component for selection management */
  @ViewChild(SmartGridComponent) smartGrid!: SmartGridComponent<OrderAttributes>;

  /** Document type for synchronization logic */
  documentType: string = "";

  /** Trip IDs for synchronization */
  TripIds: any = [];

  /** Flag for order list trip validation */
  isOrderListTrip = environment.isOrderListTrip;

  /** Flag for ERP integration */
  isErpIntegrated = environment.isErpIntegrated;

  /** Currency symbol for payment display */
  currency: string = "";

  /** Total paying amount for payment methods */
  totalPaying: number = 0;

  /** Total overpayment amount for payment methods */
  totalOverPayment: number = 0;

  /** Column definitions for order details table */
  displayedColumnsDetail: string[] = [
    "material",
    "note",
    "warehouse",
    "soldQuantity",
    "unitPrice",
    "subTotal",
    "totalSurcharges",
    "totalDiscounts",
    "tax",
    "total",
  ];

  /** Column definitions for payment methods table */
  paymentMethodsDisplayedColumns: string[] = [
    "paymentMethod",
    "bank",
    "operationNumber",
    "overpayment",
    "paying",
  ];

  /** Document classes list for validation */
  documentClassesList: DocumentClassAttributes[] = [];

  /** Material list for edit modal */
  materialList: MaterialAttributes[] = [];

  /** Sold unit of measure list for edit modal */
  soldUnitOfMeasureList: OrderDetailUnitOfMeasureAttributes[] = [];

  /** Parallel unit of measure list for edit modal */
  pararellUnitOfMeasureList: OrderDetailUnitOfMeasureAttributes[] = [];

  /** Current order detail being updated */
  currentUpdatedDetailOrder: IOrderDetailsUpdate = null;

  /** Companies list */
  companies: CompanyAttributes[] = [];

  // ============================================================================
  // SMART GRID CONFIGURATION
  // ============================================================================

  /** Column definitions for the orders grid */
  // Using 'any' due to TypeScript circular reference issue with ObjectId in NestedKeyOf type
  ordersColumnDefs: ColDef<any>[] = [];

  /** Grid configuration */
  // Using 'any' due to TypeScript circular reference issue with ObjectId in NestedKeyOf type
  ordersGridConfig: GridConfig<any> = {} as GridConfig<any>;

  // ============================================================================
  // CONSTRUCTOR (services from original component)
  // ============================================================================

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly ordersListService: OrdersListService,
    private readonly updateOrderService: UpdateOrderService,
    private readonly ordersCreationService: OrdersCreationService,
    private readonly salesOfficeService: SalesOfficeService,
    private readonly warehouseService: WarehouseService,
    private readonly documentClassService: DocumentClassService,
    private readonly companyService: CompanyService,
    private readonly snackBar: MatSnackBar,
    private readonly router: Router,
    private readonly dialog: MatDialog,
    private readonly orderPaymentService: OrderPaymentAService,
    private readonly loadService: LoadsService,
    private readonly invoiceService: InvoiceService,
    private readonly fb: FormBuilder,
    private readonly exportService: ExportService,
    private readonly _fuseNavigationService: FuseNavigationService
  ) {}

  // ============================================================================
  // LIFECYCLE HOOKS
  // ============================================================================

  ngOnInit(): void {
    this.initializeColumnDefinitions();
    this.initializeGridConfig();
    this.getCompanies();
    this.setupNavigationListener();
  }

  private setupNavigationListener(): void {
    const navigation = this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>('mainNavigation');
    if (navigation) {
      this.isNavOpened = navigation.opened;
      navigation.openedChanged
        .pipe(takeUntil(this.destroy$))
        .subscribe((opened: boolean) => {
          this.isNavOpened = opened;
          this.cdr.detectChanges();
        });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Handle input changes
    if (
      changes["summaryData"] &&
      this.summaryData &&
      this.summaryData.length > 0
    ) {
      // Obtain document type from first record (copied from original component lines 400-415)
      let documentType = this.summaryData[0]?.type?.code;
      const originalDocumentType = documentType;

      // Special type mapping
      if (documentType === "DT003") {
        documentType = "DT002"; // Send DT002 when we receive DT003
      }

      this.documentType = originalDocumentType; // Save the original value (DT003)
      this.TripIds = this.summaryData[0]?.trip?._id;

      // Fetch document classes and validate requirements
      if (documentType) {
        this.fetchDocumentClass(documentType);
      }

      // Update grid data when summaryData changes
      this.cdr.markForCheck();
    }

    if (
      changes["isAllColumnsMerma"] ||
      changes["isAllColumnsTransport"] ||
      changes["isAllColumns"]
    ) {
      // Update column definitions when column display flags change
      this.updateColumnDefs();
      this.cdr.markForCheck();
    }

    // TODO: Implement context passing to cell renderers
    // Grid config doesn't support context property
    // Need to implement alternative way to pass canEdit/canRelease to cell renderers
    // Possible solutions: service injection, column params, or custom implementation
    if (changes["canEdit"] || changes["canRelease"] || changes["canJoin"]) {
      // Mark for check to update header renderer on next cycle
      this.cdr.markForCheck();
    }
  }

  ngAfterViewInit(): void {
    // After view initialization
    // Set up any ViewChild references if needed
  }

  ngOnDestroy(): void {
    // Cleanup subscriptions
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ============================================================================
  // SELECTION MANAGEMENT
  // ============================================================================

  /**
   * Clear selection in both component SelectionModel and SmartGrid
   * This ensures checkboxes are unchecked in the UI
   */
  private clearSelection(): void {
    this.selection.clear();
    if (this.smartGrid) {
      this.smartGrid.deselectAll();
    }
    this.cdr.detectChanges();
  }

  // ============================================================================
  // INITIALIZATION METHODS
  // ============================================================================

  /**
   * Initialize column definitions for the orders grid
   */
  private initializeColumnDefinitions(): void {
    this.ordersColumnDefs = [
      // Column 4: Combined Status Icons (Status + StatusERP + Type)
      {
        colId: "statusIcons",
        headerName: "",
        cellRenderer: OrderStatusIconsCellRendererComponent,
        sortable: false,
        filterable: false,
        width: 120,
        pinned: "left",
      },

      // Column 4: Business Partner (using BusinessPartnerDisplayComponent)
      {
        field: "soldTo.name" as any,
        pinned: "left",
        headerName: "Pagador/Receptor",
        cellRenderer: BusinessPartnerCellRendererComponent,
        filterable: true,
        sortable: true,
        minWidth: 360,
      },

      // Column 7: Document Number
      {
        field: "serial",
        headerName: "Nro. Doc.",
        cellRenderer: DocumentNumberCellRendererComponent,
        filterable: true,
        sortable: true,
        minWidth: 200,
      },

      // Column 8: Class
      {
        field: "class.name",
        headerName: "Clase",
        cellRenderer: DocumentClassCellRendererComponent,
        filterable: true,
        sortable: true,
        minWidth: 180,
      },

      // Column 9: SubTotal
      {
        field: "subTotal",
        headerName: "SubTotal",
        cellRenderer: DecimalCellRendererComponent,
        sortable: true,
        filterable: false,
        minWidth: 140,
      },

      // Column 10: Total
      {
        field: "total",
        headerName: "Total",
        cellRenderer: DecimalCellRendererComponent,
        sortable: true,
        filterable: false,
        minWidth: 140,
      },

      // Column 11: Creation
      {
        field: "creationDateTime",
        headerName: "Creación",
        cellRenderer: DateTimeCellRendererComponent,
        filterable: true,
        sortable: true,
        minWidth: 140,
      },

      // Column 12: Load/Delivery DateTime (conditional - will be shown/hidden via updateColumnDefs)
      {
        field: "loadDateTime",
        headerName: "Carga y\nEntrega",
        cellRenderer: LoadDeliveryDateCellRendererComponent,
        filterable: true,
        sortable: true,
        minWidth: 200,
        hide: !this.isAllColumns,
      },

      // Column 13: Actions
      {
        colId: "actions",
        headerName: "",
        cellRenderer: OrderActionsCellRendererComponent,
        cellRendererParams: {
          canEdit: this.canEdit,
          canRelease: this.canRelease,
          onAction: (action: string, row: any) =>
            this.handleOrderAction(action, row),
          selectedCount: () => this.selection.selected.length,
          firstItemSelected: () => this.selection.selected?.[0] || null,
        },
        headerRenderer: GlobalActionsCellRendererComponent,
        headerRendererParams: {
          // Custom params must be nested in 'params' property for HeaderRendererParams
          params: {
            documentType: () => this.documentType, // Changed to function for reactivity
            selectedCount: () => this.selection.selected.length,
            canJoin: () => this.canJoin, // Changed to function for reactivity
            canRelease: this.canRelease,
            isGlobalSynchronizeAvailable: () =>
              this.isGlobalSynchronizeAvailable,
            isNoOrderSelectionJoin: () => this.isNoOrderSelectionJoin(),
            isNoOrderSelectionRelease: () => this.isNoOrderSelectionRelease(),
            onAction: (action: string) => this.handleGlobalAction(action),
          },
        },
        sortable: false,
        filterable: false,
        width: 50,
        pinned: "right",
      },
    ];
  }

  /**
   * Initialize grid configuration
   */
  private initializeGridConfig(): void {
    this.ordersGridConfig = {
      // Row identification
      rowIdField: "_id",

      // Pagination
      pagination: {
        mode: "api",
        pageSize: this.pageSize,
        pageSizeOptions: [25, 50, 100, 200],
      },

      // Column filters
      columnFilters: {
        enabled: true,
        mode: "local",
      },

      // Selection
      selection: {
        enabled: true,
        mode: "multiple",
        showCheckboxColumn: true,
        selectableField: "isSelectable", // Use the pre-calculated field from validateRequirements()
      },

      // Row expansion
      rowExpansion: {
        enabled: true,
        expandOnClick: false,
        multipleExpand: false,
      },

      // Styling
      styling: {
        maxHeight: "80vh !important",
        density: "compact",
        fontSize: "12px !important",
        cellPaddingX: "3px",
        cellPaddingY: "0px",
        stripedRows: false,
        rowHover: true,
        showBorders: true,
      },

      // Sorting
      sorting: {
        enabled: true,
        mode: "local",
        multiColumn: true,
        defaultSort: [{ field: "creationDateTime" as any, direction: "desc" }],
      },

      settingsPanel: {
        enabled: true,
        position: "right",
        enableColumnVisibility: true,
        enableDensity: false,
        enableExport: false,
        defaultDensity: "comfortable",
      },

      // Empty state
      emptyState: {
        message: "No hay pedidos para mostrar",
      },

      // TODO: Implement context passing to cell renderers
      // GridConfig doesn't support context - need alternative solution
    };
  }

  /**
   * Update column definitions based on conditional display flags
   * Matches logic from original component ngOnChanges (lines 364-398)
   */
  private updateColumnDefs(): void {
    if (!this.ordersColumnDefs || this.ordersColumnDefs.length === 0) {
      return;
    }

    // Find loadDateTime column index
    const loadDateTimeIndex = this.ordersColumnDefs.findIndex(
      (col) => col.field === "loadDateTime"
    );

    if (this.isAllColumnsMerma) {
      // Merma mode: hide loadDateTime, show documentNumberInvoice
      if (loadDateTimeIndex >= 0) {
        this.ordersColumnDefs[loadDateTimeIndex].hide = true;
      }

      // Add documentNumberInvoice column if not exists
      const hasInvoiceColumn = this.ordersColumnDefs.some(
        (col) => col.field === ("documentNumberInvoice" as any)
      );
      if (!hasInvoiceColumn) {
        // Insert after businessPartner column
        const businessPartnerIndex = this.ordersColumnDefs.findIndex(
          (col) => col.field === ("soldTo.name" as any)
        );
        if (businessPartnerIndex >= 0) {
          this.ordersColumnDefs.splice(businessPartnerIndex + 1, 0, {
            field: "documentNumberInvoice" as any,
            headerName: "Nro. Factura",
            filterable: true,
            sortable: true,
            minWidth: 120,
          });
        }
      }
    } else if (this.isAllColumnsTransport) {
      // Transport mode: show loadDateTime, show documentT
      if (loadDateTimeIndex >= 0) {
        this.ordersColumnDefs[loadDateTimeIndex].hide = false;
      }

      // Add documentT column if not exists
      const hasDocumentTColumn = this.ordersColumnDefs.some(
        (col) => col.field === ("documentT" as any)
      );
      if (!hasDocumentTColumn) {
        // Insert after documentNumber column
        const documentNumberIndex = this.ordersColumnDefs.findIndex(
          (col) => col.field === "serial"
        );
        if (documentNumberIndex >= 0) {
          this.ordersColumnDefs.splice(documentNumberIndex + 1, 0, {
            field: "documentT" as any,
            headerName: "Doc. T",
            filterable: true,
            sortable: true,
            minWidth: 100,
          });
        }
      }

      // Show creation column
      const creationIndex = this.ordersColumnDefs.findIndex(
        (col) => col.field === "creationDateTime"
      );
      if (creationIndex >= 0) {
        this.ordersColumnDefs[creationIndex].hide = false;
      }
    } else {
      // Default mode: show loadDateTime based on isAllColumns flag
      if (loadDateTimeIndex >= 0) {
        this.ordersColumnDefs[loadDateTimeIndex].hide = !this.isAllColumns;
      }

      // Remove special columns if they exist
      this.ordersColumnDefs = this.ordersColumnDefs.filter(
        (col) =>
          col.field !== ("documentNumberInvoice" as any) &&
          col.field !== ("documentT" as any)
      );
    }

    // Trigger change detection
    this.ordersColumnDefs = [...this.ordersColumnDefs];
  }

  // ============================================================================
  // EVENT HANDLERS
  // ============================================================================

  /**
   * Handle pagination changes from the grid
   * Emits pageChanged event to parent component
   */
  onPageChanged(event: any): void {
    // Convert smart-grid pagination event to Material PageEvent format
    const pageEvent: PageEvent = {
      pageIndex: event.currentPage - 1, // Smart-grid uses 1-based, Material uses 0-based
      pageSize: event.pageSize,
      length: event.totalItems || this.totalItems,
    };

    this.pageChanged.emit(pageEvent);
  }

  /**
   * Handle selection changes from the grid
   * Updates internal selection model and validates global actions availability
   */
  onSelectionChanged(event: any): void {
    // Update the selection model with the new selected rows
    this.selection.clear();
    if (event.selectedRows && event.selectedRows.length > 0) {
      // Only select rows that are selectable (based on isSelectable property)
      event.selectedRows.forEach((row: OrderAttributes) => {
        // Additional validation: only select if row is selectable
        // Using type assertion since isSelectable is added dynamically in validateRequirements
        if ((row as any).isSelectable) {
          this.selection.select(row);
        }
      });
    }

    // Check if global synchronize is available
    // Based on original component logic (table-load-list.component.ts:593-596)
    this.checkGlobalSynchronizeAvailable();

    // Re-initialize column definitions to trigger header update
    // This ensures the global actions menu updates when selection changes
    this.initializeColumnDefinitions();

    // Trigger change detection - use detectChanges to force immediate update
    this.cdr.detectChanges();
  }

  /**
   * Check if global synchronize action is available
   * All selected items must be synchronizable
   */
  private checkGlobalSynchronizeAvailable(): void {
    const selected = this.selection.selected;
    this.isGlobalSynchronizeAvailable =
      selected.length > 0 &&
      selected.every((order) => this.validateDataToSynchronize(order));
  }

  /**
   * Check if no valid orders are selected for join action
   * Returns true if no orders can be joined (disables the button)
   */
  isNoOrderSelectionJoin(): boolean {
    const selected = this.selection.selected;
    if (selected.length === 0) return true;

    // Check if any selected order can be joined
    const canJoinAny = selected.some((order: any) => order.canJoin === true);
    return !canJoinAny;
  }

  /**
   * Check if no valid orders are selected for release action
   * Returns true if no orders can be released (disables the button)
   */
  isNoOrderSelectionRelease(): boolean {
    const selected = this.selection.selected;
    if (selected.length === 0) return true;

    // Check if any selected order can be released
    const canReleaseAny = selected.some(
      (order: any) => order.canRelease === true
    );
    return !canReleaseAny;
  }

  /**
   * Check if all selectable rows are selected
   * Based on original component (table-load-list.component.ts:564-570)
   */
  isAllSelected(): boolean {
    const validRows = this.summaryData.filter(
      (row) =>
        this.validateDataToSynchronize(row) || this.validateDataToRelease(row)
    );
    const numSelected = this.selection.selected.length;
    const numValidRows = validRows.length;
    return numSelected === numValidRows && numValidRows > 0;
  }

  /**
   * Master toggle to select/deselect all selectable rows
   * Based on original component (table-load-list.component.ts:572-578)
   */
  masterToggle(): void {
    if (this.isAllSelected()) {
      this.selection.clear();
    } else {
      // Only select rows that pass validation
      this.summaryData.forEach((row) => {
        if (
          this.validateDataToSynchronize(row) ||
          this.validateDataToRelease(row)
        ) {
          this.selection.select(row);
        }
      });
    }
    // Update global synchronize availability after toggling
    this.checkGlobalSynchronizeAvailable();
    this.cdr.markForCheck();
  }

  /**
   * Handle refresh request
   * Emits refreshMonitor event to parent
   */
  onRefreshRequested(): void {
    this.refreshMonitor.emit();
  }

  /**
   * Handle row expansion state change
   * Sets the selected order and loads detail data for the expanded row
   * Copied and adapted from original component toggleDetails() method (lines 607-657)
   */
  async onRowExpansionChanged(event: {
    data: OrderAttributes;
    expanded: boolean;
    expandedRows: OrderAttributes[];
  }): Promise<void> {
    const order = event.data;
    const orderId = order._id?.toString() || "";

    // If collapsed, remove data for this order
    if (!event.expanded) {
      this.expandedRowDetails.delete(orderId);
      this.expandedRowPayments.delete(orderId);

      // Clear selectedOrder if it's the collapsed row
      if (this.selectedOrder?._id?.toString() === orderId) {
        this.selectedOrder = null;
      }

      this.cdr.markForCheck();
      return;
    }

    // Set selected order for the expanded row
    this.selectedOrder = order;

    // Store order details for this specific row
    this.expandedRowDetails.set(orderId, order.detail || []);
    this.dataSourceDetail.data = order.detail || [];

    // Load payments for selected invoice (API call)
    this.loadOrderPayments(order?.serial, orderId);

    // Set currency from company
    const companyFound: CompanyAttributes = this.companies.find(
      (c) => c.id == order.company?.id
    );
    if (companyFound) {
      order.company = companyFound;
    }
    this.currency = companyFound?.currencyBase?.code ?? "";

    // Get warehouses and document class
    const orderStatusId = order.statusId;
    if (orderStatusId != 0 && orderStatusId != 7 && orderStatusId != 8) {
      // Get document class details (API call)
      await this.getDocumentClass(order);

      // Get warehouses and stock if needed
      if (
        order.class["showCurrentStockForMaterials"] == true &&
        !order["warehousesToUpdate"]
      ) {
        try {
          // Get sales office details (API call)
          const salesOffice: Partial<SalesOfficeAttributes> =
            await lastValueFrom(
              this.salesOfficeService.findOne(order.salesOffice._id.toString())
            ).then((res) => res.data);

          if (salesOffice && salesOffice.warehouses) {
            // Get warehouses filtered by sales office (API call)
            const filter = {
              officeWarehouse: salesOffice.warehouses.map((ware) => ware._id),
              company: [environment.companyId],
            };
            order["warehousesToUpdate"] = await lastValueFrom(
              this.warehouseService.filterComboBox(filter)
            ).then((res) => res.data);

            // Get stock for materials (API call)
            this.getStock(order, order["warehousesToUpdate"]);
          }
        } catch (err: any) {
          this.snackBar.open(
            err.error?.message || "Error loading warehouses",
            "Cerrar",
            { duration: 3000 }
          );
        }
      }
    }

    // Trigger change detection
    this.cdr.markForCheck();
  }

  /**
   * Handle order action from cell renderer
   * Routes actions to appropriate methods
   * @param action - The action type
   * @param row - The order data
   */
  handleOrderAction(action: string, row: OrderAttributes): void {
    switch (action) {
      case "synchronize":
        this.synchronizeOrders(row);
        break;
      case "move":
        this.onUpdateLoadDocument(row);
        break;
      case "disunify":
        this.disunifyOrderModal(row);
        break;
      case "release":
        this.openReleaseOrders(row);
        break;
      case "viewLogs":
        this.openLogModal(row);
        break;
      case "reprintInvoice":
        this.reprintInvoice(row);
        break;
      default:
        console.warn(`Unknown action: ${action}`);
    }
  }

  /**
   * Handle global actions from the global actions cell renderer
   * These are bulk operations on selected rows
   * @param action - Action type
   */
  handleGlobalAction(action: string): void {
    switch (action) {
      case "synchronize":
        // Synchronize all selected orders
        this.synchronizeOrders(null); // null means use selection
        break;
      case "move":
        // Move/update selected orders
        this.onUpdateOrder();
        break;
      case "unify":
        // Unify selected orders (join)
        this.openMConfirmDialog();
        break;
      case "release":
        // Release selected orders
        this.openReleaseOrders();
        break;
      default:
        console.warn(`Unknown global action: ${action}`);
    }
  }

  // ============================================================================
  // ACTION METHODS (from original component)
  // ============================================================================

  /**
   * Synchronize orders with ERP
   * Copied from original component (lines 759-882)
   */
  synchronizeOrders(order: OrderAttributes): void {
    let dataToSendPost = {
      TripIds: [],
      orderIds: [],
      method: "POST",
      documentsType: [],
    };

    let dataToSendPut = {
      TripIds: [],
      orderIds: [],
      method: "PUT",
      documentsType: [],
    };

    // Data for non-DT003 service
    let dataToSendOtherPost = {
      TripIds: [],
      orderIds: [],
      method: "POST",
      documentsType: [this.documentType],
    };

    let dataToSendOtherPut = {
      TripIds: [],
      orderIds: [],
      method: "PUT",
      documentsType: [this.documentType],
    };

    const isDT003 = (item: OrderAttributes) => {
      return item.type.code === "DT003";
    };

    if (order) {
      // Case 1: Individual order
      if (isDT003(order)) {
        // DT003 service
        if (order.externalKeys?.code) {
          dataToSendPut.orderIds.push(order._id.toString());
        } else {
          dataToSendPost.orderIds.push(order._id.toString());
        }
      } else {
        // Other service for non-DT003
        if (order.externalKeys?.code) {
          dataToSendOtherPut.orderIds.push(order._id.toString());
        } else {
          dataToSendOtherPost.orderIds.push(order._id.toString());
        }
      }
    } else {
      if (this.selection.selected.length > 0) {
        // Case 2: Multiple selection
        this.selection.selected.forEach((o) => {
          if (isDT003(o)) {
            // DT003 service
            if (o.externalKeys?.code) {
              dataToSendPut.orderIds.push(o._id.toString());
            } else {
              dataToSendPost.orderIds.push(o._id.toString());
            }
          } else {
            // Other service for non-DT003
            if (o.externalKeys?.code) {
              dataToSendOtherPut.TripIds.push(o.trip._id.toString());
            } else {
              dataToSendOtherPost.TripIds.push(o.trip._id.toString());
            }
          }
        });
      } else {
        const tripToSend = this.summaryData.find((o) => !isDT003(o));
        if (tripToSend) {
          dataToSendOtherPost.TripIds.push(this.TripIds);
        }
      }
    }

    const requests = [];

    // DT003 service
    if (dataToSendPost.orderIds.length > 0) {
      requests.push(
        this.ordersListService.synchronizeOrders(dataToSendPost).toPromise()
      );
    }
    if (dataToSendPut.orderIds.length > 0) {
      requests.push(
        this.ordersListService.synchronizeOrders(dataToSendPut).toPromise()
      );
    }

    // Other service (non-DT003)
    if (dataToSendOtherPost.TripIds.length > 0) {
      requests.push(
        this.ordersListService
          .synchronizeOrdersOther(dataToSendOtherPost)
          .toPromise()
      );
    }
    if (dataToSendOtherPut.TripIds.length > 0) {
      requests.push(
        this.ordersListService
          .synchronizeOrdersOther(dataToSendOtherPut)
          .toPromise()
      );
    }

    if (requests.length === 0) {
      this.snackBar.open(
        "No se encontraron registros pendientes de sincornizar con ERP.",
        "Cerrar",
        { duration: 5000 }
      );
    }

    Promise.all(requests)
      .then(() => {
        this.snackBar.open("Registro sincronizado con éxito", "Cerrar", {
          duration: 3000,
        });
        if (this.selection.selected.length > 0) this.clearSelection();
        this.refreshMonitor.emit();
      })
      .catch((err) => {
        this.snackBar.open(
          err.error.message || "Error al sincronizar con el ERP.",
          "Cerrar",
          { duration: 5000 }
        );
      });
  }

  /**
   * Move order to different trip/route
   * Copied from original component (lines 956-966)
   */
  onUpdateLoadDocument(order: OrderAttributes): void {
    const dialogRef = this.dialog.open(ChangeTripOrderComponent, {
      panelClass: "form-modal",
      data: order,
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) this.refreshMonitor.emit();
    });
  }

  /**
   * Open log modal for order
   * Copied from original component (lines 1390-1405)
   */
  openLogModal(element: OrderAttributes): void {
    const dataToModal = {
      documentIdentifier: element.serial,
    };

    const dialogRef = this.dialog.open(LogModalComponent, {
      panelClass: ".form-modal-interlocutor",
      width: "90%",
      data: dataToModal,
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe((result) => {
      // Handle result if needed
    });
  }

  /**
   * Disunify unified order
   * Copied from original component (lines 1934-1986)
   */
  disunifyOrderModal(order?: OrderAttributes): void {
    if (!order || !order._id) {
      this.snackBar.open("Documento no válido para desunificar.", "Cerrar", {
        duration: 3000,
      });
      return;
    }

    // Validación específica del operationType
    if (order.operationType !== 4) {
      this.snackBar.open(
        "Solo se pueden desunificar documentos unificados.",
        "Cerrar",
        { duration: 3000 }
      );
      return;
    }

    this.dialog
      .open(this.confirmDialog, {
        data: {
          title: "Desunificar documento",
          message: "¿Está seguro que desea separar este documento?",
        },
        panelClass: "delete-modal",
        disableClose: true,
      })
      .afterClosed()
      .subscribe(async (result) => {
        if (result == true) {
          try {
            // Enviamos solo el serial del documento
            await lastValueFrom(
              this.updateOrderService.desunifyOrder({
                serial: order._id.toString(),
              })
            );
            this.snackBar.open("Desunificación exitosa", "Cerrar", {
              duration: 3000,
            });
            if (this.selection.selected.length > 0) this.clearSelection();
            this.refreshMonitor.emit();
          } catch (err: any) {
            this.snackBar.open(
              err.error?.message || "Error en la desunificación",
              "Cerrar",
              { duration: 3000 }
            );
          }
        }
      });
  }

  /**
   * Reprint invoice
   * Copied from original component (lines 2019-2041)
   */
  reprintInvoice(element: OrderAttributes): void {
    if (!element._id) {
      this.snackBar.open("No se encontró el ID de la factura", "Cerrar", {
        duration: 3000,
      });
      return;
    }
    const invoiceId = element._id.toString();
    const options: InvoicePdfOptions = {
      format: "letter",
      landscape: false,
      scale: 1,
      printBackground: true,
      marginTop: "10mm",
      marginBottom: "10mm",
    };
    this.invoiceService.downloadAndSaveInvoice(invoiceId, options).subscribe({
      next: () => {
        this.snackBar.open("Factura descargada correctamente", "Cerrar", {
          duration: 3000,
        });
      },
      error: (error) => {
        this.snackBar.open(
          error.message || "Error al imprimir la factura",
          "Cerrar",
          { duration: 5000 }
        );
      },
    });
  }

  /**
   * Release orders
   * Copied from original component (lines 2058-2095)
   */
  openReleaseOrders(order?: OrderAttributes): void {
    const ordersToReleaseFiltered = this.selection.selected.filter(
      (o) => (o as any).canRelease
    );

    if (!order && ordersToReleaseFiltered.length === 0) {
      this.openConfirmDialog(
        "Los pedidos seleccionados no están permitidos para liberar."
      ).subscribe();
      return;
    }

    this.dialog
      .open(this.confirmDialog, {
        data: {
          title: "Confirmar",
          message: "¿Está seguro que desea liberar este(os) pedido(s)?",
          innerHTML: `
            <div class="text-sm text-red-600">
              Esta acción es irreversible una vez que se haya(n) liberado el(los) pedido(s).
            </div>
          `,
        },
        panelClass: "delete-modal",
        disableClose: true,
      })
      .afterClosed()
      .subscribe(async (result) => {
        if (result == true) {
          try {
            await lastValueFrom(
              this.ordersListService.releaseOrders({
                orderSerials: order
                  ? [order.serial]
                  : ordersToReleaseFiltered.map((order) => order.serial),
              })
            );
            this.snackBar.open("Liberación exitosa", "Cerrar", {
              duration: 3000,
            });
            if (this.selection.selected.length > 0) this.clearSelection();
            this.refreshMonitor.emit();
          } catch (err: any) {
            this.snackBar.open(
              err.error?.message || "Error en la Liberación",
              "Cerrar",
              { duration: 3000 }
            );
          }
        }
      });
  }

  /**
   * Open confirm dialog with custom message
   * Copied from original component (lines 2046-2056)
   */
  openConfirmDialog(message: string) {
    return this.dialog
      .open(CustomDialogComponent, {
        data: {
          title: "Advertencia",
          message: message,
          yes: "Aceptar",
        },
        panelClass: "delete-modal",
        disableClose: true,
      })
      .afterClosed();
  }

  /**
   * Update/move selected orders
   * Opens ChangeTripOrderComponent dialog for single or multiple orders
   * Based on original component (table-load-list.component.ts:923-954)
   */
  private onUpdateOrder(order?: OrderAttributes): void {
    const data =
      this.selection.selected?.length > 0
        ? this.selection.selected // Use array of selected orders
        : order; // Use single order

    if (!data || (Array.isArray(data) && data.length === 0)) {
      this.snackBar.open("No hay órdenes seleccionadas", "Cerrar", {
        duration: 3000,
      });
      return;
    }

    // Open ChangeTripOrderComponent dialog
    const dialogRef = this.dialog.open(ChangeTripOrderComponent, {
      panelClass: "form-modal",
      data: data,
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result && result.data) {
        // Update local grid data if single order was moved
        if (!Array.isArray(data)) {
          const dataTemp = this.summaryData.slice();
          const orderIndex = dataTemp.findIndex(
            (o) => String(o._id) === String(result.data._id)
          );
          if (orderIndex >= 0) {
            dataTemp[orderIndex].address = result.data.address;
            dataTemp[orderIndex].expeditionCondition = result.data.expeditionCondition;
            dataTemp[orderIndex].note = result.data.note;
            dataTemp[orderIndex].statusERP = result.data.statusERP;
            dataTemp[orderIndex].statusId = result.data.statusId;
          }
          this.summaryData = dataTemp;
          this.cdr.detectChanges();
        }
      }
      if (result) {
        // Refresh monitor to get latest data from server
        if (this.selection.selected.length > 0) this.clearSelection();
        this.refreshMonitor.emit();
      }
    });
  }

  /**
   * Open unify/join confirmation dialog
   * Based on original component (table-load-list.component.ts:1798-1834)
   * TODO: Implement full unify logic with dialog
   */
  openMConfirmDialog(): void {
    const ordersToJoinFiltered = this.selection.selected.filter(
      (order: any) => order.canJoin
    );
    if (ordersToJoinFiltered.length == 0) {
      this.openConfirmSDialog(
        "Los pedidos seleccionados no están permitidos para unificarse."
      );
      return;
    }
    this.dialog
      .open(this.confirmDialog, {
        data: {
          title: "Confirmar",
          innerHTML: `
            <p>Los siguientes pedidos pueden ser unificados:</p>
            <ul>
              ${ordersToJoinFiltered.map((order) => `<li>${order.serial}</li>`).join("")}
            </ul>
            <p>¿Está seguro que desea unificarlos?</p>
          `,
        },
        panelClass: "delete-modal",
        disableClose: true,
      })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          this.validateJoinOrderModal(ordersToJoinFiltered);
        }
      });
  }

  openConfirmSDialog(message: string): void {
    this.dialog
      .open(this.confirmDialog, {
        data: { title: "Advertencia", message: message },
        panelClass: "delete-modal",
        disableClose: true,
      })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
        }
      });
  }

  /**
   * Load companies data
   * Copied from original component (lines 723-732)
   */
  async getCompanies(): Promise<void> {
    this.companies = [];
    try {
      const result = await lastValueFrom(
        this.companyService.fetchCompanyByEnvIds()
      );
      this.companies = result.data;
    } catch (err: any) {
      this.snackBar.open(
        err.error?.message || "Error loading companies",
        "Cerrar",
        { duration: 3000 }
      );
    }
  }

  /**
   * Fetch document classes for the given document type
   * Copied from original component (lines 734-744)
   * @param documentType - Document type code (e.g., 'DT002', 'DT003')
   */
  private fetchDocumentClass(documentType: string): void {
    const dataToFilter = {
      type: documentType,
    };

    this.ordersListService
      .fetchDocumentClasses(dataToFilter)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.documentClassesList = res.data;
          this.validateRequirements();
          this.cdr.markForCheck();
        },
        error: (err) => {
          this.snackBar.open(
            err.error?.message || "Error loading document classes",
            "Cerrar",
            { duration: 3000 }
          );
        },
      });
  }

  /**
   * Validate if order can be synchronized with ERP
   * Copied from original component (lines 1407-1421)
   * @param order - Order to validate
   * @returns True if order can be synchronized
   */
  validateDataToSynchronize(order: OrderAttributes): boolean {
    if (this.documentType === "DT003") {
      return (
        !!order &&
        (order.statusERP === 0 ||
          order.statusERP === 4 ||
          order.statusERP === 5 ||
          order.statusERP === 6) &&
        (order.statusId === 1 || order.statusId === 6) &&
        !!order.trip &&
        (!this.isOrderListTrip || !!order.trip?.externalKeys?.code)
      );
    }
    return validateOrdersByAction([order], this.documentClassesList, "SYNC");
  }

  /**
   * Pre-calculate validation requirements for all orders
   * Extended from original component's validateRequirements() (lines 1761-1781)
   * Adds canSynchronize, canReprintInvoice, and isSelectable to each order
   */
  private validateRequirements(): void {
    if (!this.summaryData || this.summaryData.length === 0) {
      return;
    }

    this.summaryData = this.summaryData.map((order) => {
      // Existing validations from original component
      const canJoin = validateOrdersByAction(
        [order],
        this.documentClassesList,
        "JOIN"
      );

      const docClass = this.documentClassesList.find(
        (d) => d._id?.toString() === order.class?._id?.toString()
      );

      const canRelease = this.validateDataToRelease(order);

      // Pre-calculate validations for cell renderer actions
      const canSynchronize = this.validateDataToSynchronize(order);
      const canReprintInvoice = this.invoiceService.canReprintInvoice(order);

      // NEW: Determine if row is selectable
      // Based on original component logic (table-load-list.component.html:41)
      // Checkbox is enabled if any of these conditions are true
      const isSelectable = canSynchronize || canJoin || canRelease;

      return {
        ...order,
        canJoin,
        canUnifyConditions: docClass?.canUnifyConditions ?? false,
        canRelease,
        canSynchronize,
        canReprintInvoice,
        isSelectable, // NEW: For checkbox enable/disable logic
      } as any;
    });

    this.cdr.markForCheck();
  }

  // ============================================================================
  // API CALLS FOR ROW EXPANSION
  // ============================================================================

  /**
   * Load order payments from API
   * Copied from original component (lines 1988-2017)
   * @param serialInvoice - Invoice serial number
   * @param orderId - Order ID for storing payment data
   */
  private loadOrderPayments(serialInvoice: string, orderId?: string): void {
    if (!serialInvoice) {
      this.paymentMethodsDataSource.data = [];
      if (orderId) {
        this.expandedRowPayments.set(orderId, []);
      }
      return;
    }

    this.orderPaymentService
      .findPaymentsByInvoiceSerial(serialInvoice)
      .subscribe({
        next: (response) => {
          const payments = response?.data?.[0]?.data || [];

          this.totalPaying = this.calculateTotalMethod(payments, "paying");
          this.totalOverPayment = this.calculateTotalMethod(
            payments,
            "overpayment"
          );

          const groupedPayments = this.groupPayments(payments);
          this.paymentMethodsDataSource.data = groupedPayments;

          // Store payments for this specific row
          if (orderId) {
            this.expandedRowPayments.set(orderId, groupedPayments);
          }

          // Trigger change detection
          this.cdr.markForCheck();
        },
        error: (error) => {
          this.snackBar.open(
            error.error?.message || "Error loading payments.",
            "Cerrar",
            { duration: 5000 }
          );
          this.paymentMethodsDataSource.data = [];
          if (orderId) {
            this.expandedRowPayments.set(orderId, []);
          }
          this.cdr.markForCheck();
        },
      });
  }

  /**
   * Get document class details from API
   * Copied from original component (lines 1378-1384)
   * @param order - Order to get document class for
   */
  async getDocumentClass(order: OrderAttributes): Promise<void> {
    try {
      const documentClass: DocumentClassAttributes = await lastValueFrom(
        this.documentClassService.findOne(String(order.class._id))
      ).then((res) => res.data);
      if (documentClass) {
        order.class = documentClass;
      }
      this.cdr.markForCheck();
    } catch (err: any) {
      this.snackBar.open(
        err.error?.message || "Error loading document class",
        "Cerrar",
        { duration: 3000 }
      );
    }
  }

  /**
   * Get stock for order materials from API
   * Copied from original component (lines 1328-1376)
   * @param order - Order to get stock for
   * @param warehouses - List of warehouses to check stock
   */
  async getStock(
    order: OrderAttributes,
    warehouses: WarehouseAttributes[]
  ): Promise<void> {
    try {
      const dataToSend = {
        materialsIds: order.detail
          .filter((od) => od.statusId == 1)
          .map((d) => String(d.material._id)),
        unitMeasureIds: [],
        warehouseIds: warehouses.map((w) => String(w._id)),
      };

      const stock: MaterialStockAttributes[] = await lastValueFrom(
        this.ordersCreationService.fetchStock(dataToSend)
      ).then((res) => res.data[0].data);

      const dateToValidate = new Date();
      dateToValidate.setMonth(
        dateToValidate.getMonth() +
          (order.company["totalMonthsToGroupStockInRouting"] ?? 0)
      );

      order.detail.forEach((od) => {
        od["stock"] = stock.filter(
          (s) => String(s.materialId) == String(od.material._id)
        );
        od["stockToValidate"] = this.roundToX(
          (order.company["groupStockByDateInRouting"] == true
            ? od["stock"]
                .filter(
                  (s: MaterialStockAttributes) =>
                    String(s.warehouseId) == String(od.warehouse._id) &&
                    String(s.unitOfMeasureId) ==
                      String(od.soldUnitOfMeasure._id) &&
                    s.lotExpirationDate &&
                    new Date(s.lotExpirationDate).getTime() >=
                      dateToValidate.getTime()
                )
                .reduce((a, b) => a + b.availableStock, 0)
            : od["stock"].find(
                (s: MaterialStockAttributes) =>
                  String(s.warehouseId) == String(od.warehouse._id) &&
                  String(s.unitOfMeasureId) == String(od.soldUnitOfMeasure._id)
              )?.availableStock) ?? 0,
          3
        );
      });

      this.cdr.markForCheck();
    } catch (err: any) {
      this.snackBar.open(
        err.error?.message || "Error loading stock",
        "Cerrar",
        { duration: 3000 }
      );
    }
  }

  // ============================================================================
  // EXPANSION TEMPLATE HELPER METHODS
  // ============================================================================

  /**
   * Get order details for a specific order
   * @param order - Order to get details for
   * @returns Order detail data array
   */
  getOrderDetails(order: OrderAttributes): OrderDetailAttributes[] {
    const orderId = order._id?.toString() || "";
    return this.expandedRowDetails.get(orderId) || order.detail || [];
  }

  /**
   * Get payment methods for a specific order
   * @param order - Order to get payments for
   * @returns Payment data array
   */
  getOrderPayments(order: OrderAttributes): OrderPaymentAttributes[] {
    const orderId = order._id?.toString() || "";
    return this.expandedRowPayments.get(orderId) || [];
  }

  /**
   * Compare two items by their _id property
   * Used for mat-select compareWith
   */
  compareById(item1: any, item2: any): boolean {
    return item1 && item2 ? item1._id === item2._id : item1 === item2;
  }

  /**
   * Get extra status name from order ERP status list
   * @param list - Array of order extra status attributes
   * @param type - Type of status to retrieve (GLOBAL, REJECT, DELIVERY, CREDIT, INVOICE, USER)
   * @returns Status description or "-"
   */
  getExtraStatusName(list: OrderExtraStatusAttributes[], type: string): string {
    const status = list?.find((i) => i.type == type);
    return status?.description || "-";
  }

  /**
   * Validate if order can be edited
   * Uses document class validation utility
   * @param order - Order to validate
   * @returns True if order can be edited
   */
  validateDataToEdit(order: OrderAttributes): boolean {
    return validateOrdersByAction([order], this.documentClassesList, "EDIT");
  }

  /**
   * Validate if order can be released
   * @param order - Order to validate
   * @returns True if order can be released
   */
  validateDataToRelease(order: OrderAttributes): boolean {
    return validateOrdersByAction(
      [order],
      this.documentClassesList,
      ActionRequirementEnum.RELEASE
    );
  }

  /**
   * Open note detail modal for order detail
   * @param selectedOrder - The order containing the detail
   * @param detail - The order detail with the note
   */
  openNoteDetail(
    selectedOrder: OrderAttributes,
    detail: OrderDetailAttributes
  ): void {
    if (!detail.note || detail.note.trim() === "") {
      return;
    }
    const modalData = {
      order: selectedOrder,
      detail: detail,
      note: detail.note,
      material: detail.material,
    };
    this.dialog.open(NoteDetailModalComponent, {
      data: modalData,
      width: "550px",
      maxWidth: "95vw",
      disableClose: true,
      panelClass: "custom-dialog-container",
    });
  }

  /**
   * Open batches detail modal for order detail
   * @param order - The order
   * @param orderDetail - The order detail with batches
   */
  openBatchesDetail(
    order: OrderAttributes,
    orderDetail: OrderDetailAttributes
  ): void {
    const { type, isEdit } = this.getBatchConfig(order.type.code);
    const dialogRef = this.dialog.open(BatchesDetailComponent, {
      width: "90%",
      data: {
        order: order,
        data: orderDetail,
        type: type,
        isEdit: isEdit,
        isChangeCostBatch: false,
      },
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe((result) => {
      // Handle result if needed
    });
  }

  /**
   * Get batch configuration based on order type code
   * @param orderTypeCode - Order type code
   * @returns Batch configuration with type and isEdit flag
   */
  private getBatchConfig(orderTypeCode: string): {
    type: string;
    isEdit: boolean;
  } {
    const deliveryCodes = ["DT012", "DT013"];
    const normalCodes = ["DT009", "DT010"];

    if (orderTypeCode === "DT011") {
      return { type: "deliveryBatches", isEdit: false };
    } else if (deliveryCodes.includes(orderTypeCode)) {
      return { type: "deliveryBatches", isEdit: true };
    } else if (normalCodes.includes(orderTypeCode)) {
      return { type: "normal", isEdit: true };
    }
    return { type: "normal", isEdit: false };
  }

  /**
   * Open credit note detail modal
   * @param groupedPayment - Grouped payment with original payments
   */
  openCreditNoteDetail(groupedPayment: any): void {
    const detailDataSource = new MatTableDataSource(
      groupedPayment.originalPayments
    );
    this.dialog.open(CreditNoteDialogComponent, {
      width: "800px",
      data: detailDataSource.data,
      autoFocus: false,
    });
  }

  /**
   * Handle warehouse change in order detail
   * Updates stock validation for the detail
   * @param orderDetail - Order detail being updated
   */
  onChangeWarehouseInOrderDetail(orderDetail: OrderDetailAttributes): void {
    orderDetail["stockToValidate"] = 0;
    if (orderDetail["stock"]) {
      orderDetail["stockToValidate"] = this.roundToX(
        orderDetail["stock"]?.find(
          (s: any) => s.warehouseId == orderDetail.warehouse?._id
        )?.quantity || 0,
        3
      );
    }
  }

  /**
   * Handle price change in order detail
   * @param order - The order
   * @param orderDetail - Order detail being updated
   */
  onChangePriceInOrderDetail(
    order: OrderAttributes,
    orderDetail: OrderDetailAttributes
  ): void {
    orderDetail["thereIsChanges"] = true;
    if (orderDetail.unitPrice < 0) {
      orderDetail.unitPrice = 0;
      this.snackBar.open("El precio no debe ser menor a cero.", "Cerrar", {
        duration: 3000,
      });
    }
    // Recalculate subtotal if needed
    // this.onChangeSubTotalInOrderDetail(order, orderDetail);
  }

  /**
   * Initialize update delivery DTO from order detail
   * Copied from original component (lines 968-978)
   * @param item - Order detail to initialize from
   * @returns Update DTO with order detail data
   */
  private initializeUpdateDelivery(
    item: OrderDetailAttributes
  ): IOrderDetailsUpdate {
    return {
      materialId: item.material._id.toString(),
      baseUnitOfMeasureId: item.soldUnitOfMeasure._id.toString(),
      pararellUnitOfMeasureId: item.pararellUnitOfMeasure._id.toString(),
      soldQuantity: this.roundToX(item.soldQuantity, 3),
      unitPrice: item.unitPrice,
    };
  }

  /**
   * Validate input for quantity field
   * Ensures quantity is a valid number with max 3 decimal places
   * Copied from original component (lines 980-989)
   * @param event - Input event from quantity field
   */
  public validateInput(event: any): void {
    let value = event.target.value;
    let numericValue = parseFloat(value);
    numericValue = parseFloat(numericValue.toFixed(3)) ?? 0;
    event.target.value = numericValue;
    this.currentUpdatedDetailOrder.soldQuantity = numericValue;
  }

  /**
   * Recalculate order detail subtotal and totals
   * Handles discounts, surcharges, and taxes
   * Copied from original component (lines 1128-1178)
   * @param order - The order containing the detail
   * @param orderDetail - Order detail being updated
   */
  onChangeSubTotalInOrderDetail(
    order: OrderAttributes,
    orderDetail: OrderDetailAttributes
  ): void {
    orderDetail["thereIsChanges"] = true;
    if (orderDetail.soldQuantity < 0) {
      orderDetail.soldQuantity = 0;
      this.snackBar.open("La cantidad no debe ser menor a cero.", "Cerrar", {
        duration: 3000,
      });
    }

    orderDetail.subTotal = orderDetail.unitPrice * orderDetail.soldQuantity;

    // Calculate discounts
    orderDetail.totalDiscounts = 0;
    if (orderDetail.discounts && orderDetail.discounts.length > 0) {
      orderDetail.totalDiscounts = orderDetail.discounts.reduce(
        (acc, discount) => {
          switch (discount.type) {
            case "PERCENTAGE":
              return acc + (orderDetail.subTotal * discount.discount) / 100;
            case "AMOUNT":
              return acc + discount.discount;
            default:
              return acc;
          }
        },
        0
      );
    }

    orderDetail.subTotalBeforeTaxes =
      orderDetail.subTotal -
      orderDetail.totalDiscounts +
      orderDetail.totalSurcharges;

    // Calculate taxes
    orderDetail.totalTaxes = 0;
    if (orderDetail.taxes && orderDetail.taxes.length > 0) {
      orderDetail.totalTaxes = orderDetail.taxes.reduce(
        (acc, tax) =>
          acc + (orderDetail.subTotalBeforeTaxes * tax.percentage) / 100,
        0
      );
    }

    orderDetail.total =
      orderDetail.subTotalBeforeTaxes + orderDetail.totalTaxes;
    this.calculateOrderTotals(order);
  }

  /**
   * Recalculate order totals from all details
   * Sums up all active detail totals
   * Copied from original component (lines 1180-1199)
   * @param order - Order to recalculate totals for
   */
  calculateOrderTotals(order: OrderAttributes): void {
    order.subTotal = 0;
    order.totalDiscounts = 0;
    order.totalSurcharges = 0;
    order.subTotalBeforeTaxes = 0;
    order.totalTaxes = 0;
    order.total = 0;

    order.detail.forEach((od) => {
      if (od.statusId == 1) {
        order.subTotal += od.subTotal;
        order.totalDiscounts += od.totalDiscounts;
        order.totalSurcharges += od.totalSurcharges;
        order.subTotalBeforeTaxes += od.subTotalBeforeTaxes;
        order.totalTaxes += od.totalTaxes;
        order.total += od.total;
      }
    });
    this.cdr.detectChanges();
  }

  /**
   * Handle quantity change in order detail
   * Validates against stock if needed and recalculates totals
   * Copied from original component (lines 1201-1219)
   * @param order - The order
   * @param orderDetail - Order detail being updated
   */
  onChangeQuantityInOrderDetail(
    order: OrderAttributes,
    orderDetail: OrderDetailAttributes
  ): void {
    orderDetail["thereIsChanges"] = true;
    if (
      order.class["makesStockValidation"] &&
      orderDetail["stockToValidate"] &&
      orderDetail.soldQuantity > orderDetail["stockToValidate"]
    ) {
      orderDetail.soldQuantity = orderDetail["stockToValidate"];
      this.snackBar.open(
        "La cantidad no debe ser mayor al disponible.",
        "Cerrar",
        { duration: 3000 }
      );
    }
    this.onChangeSubTotalInOrderDetail(order, orderDetail);
  }

 
  /**
   * Open confirmation dialog before saving order detail updates
   * Copied from original component (lines 1235-1252)
   * @param order - The order
   * @param orderDetail - Order detail being updated
   */
  openOrderDetailUpdateConfirmation(
    order: OrderAttributes,
    orderDetail: OrderDetailAttributes
  ): void {
    if (orderDetail["thereIsChanges"]) {
      this.dialog
        .open(this.confirmDialog, {
          data: {
            message: "¿Está seguro que desea guardar los cambios?",
          },
          panelClass: "form-modal",
          disableClose: true,
        })
        .afterClosed()
        .subscribe((result) => {
          if (result) this.onSaveOrderDetailUpdate(order, orderDetail);
        });
    } else {
      orderDetail["isEditMode"] = false;
    }
    this.cdr.detectChanges();
  }

  /**
   * Save order detail updates to API
   * Calls update service and refreshes local data
   * Copied and adapted from original component (lines 1254-1326)
   * @param order - The order
   * @param orderDetail - Order detail being saved
   */
  onSaveOrderDetailUpdate(
    order: OrderAttributes,
    orderDetail: OrderDetailAttributes
  ): void {
    const dataToUpdate: IUpdateOrder = {
      detail: [
        {
          position: orderDetail.position,
          warehouseId: orderDetail.warehouse
            ? String(orderDetail.warehouse._id)
            : null,
          soldQuantity: orderDetail.soldQuantity,
          unitPrice: orderDetail.unitPrice,
        },
      ],
    };

    this.updateOrderService
      .updateOrder(String(order._id), dataToUpdate)
      .subscribe({
        next: (result) => {
          this.snackBar.open("Detalle actualizado con éxito.", "Cerrar", {
            duration: 3000,
          });
          orderDetail["isEditMode"] = false;
          orderDetail["thereIsChanges"] = false;

          if (result && result.data) {
            // Update selectedOrder details with preserved stock data
            if (
              this.selectedOrder &&
              String(this.selectedOrder._id) == String(order._id)
            ) {
              const detailTemp: OrderDetailAttributes[] =
                result.data.detail.map((d) => {
                  const detailFoundInLocal = this.selectedOrder.detail.find(
                    (od) => od.position == d.position
                  );
                  d["stock"] = detailFoundInLocal
                    ? detailFoundInLocal["stock"]
                    : [];
                  d["stockToValidate"] = detailFoundInLocal
                    ? detailFoundInLocal["stockToValidate"]
                    : undefined;
                  return d;
                });
              this.selectedOrder.detail = detailTemp;
              this.dataSourceDetail.data = this.selectedOrder.detail;
            }

            // Update smart-grid data
            const dataTemp = this.summaryData.slice();
            const orderIndex = dataTemp.findIndex(
              (o) => String(o._id) === String(result.data._id)
            );
            if (orderIndex >= 0) {
              dataTemp[orderIndex].subTotal = result.data.subTotal;
              dataTemp[orderIndex].totalDiscounts = result.data.totalDiscounts;
              dataTemp[orderIndex].totalSurcharges =
                result.data.totalSurcharges;
              dataTemp[orderIndex].subTotalBeforeTaxes =
                result.data.subTotalBeforeTaxes;
              dataTemp[orderIndex].totalTaxes = result.data.totalTaxes;
              dataTemp[orderIndex].total = result.data.total;
              dataTemp[orderIndex].detail = result.data.detail;
              dataTemp[orderIndex].statusERP = result.data.statusERP;
            }
            this.summaryData = dataTemp;
            this.cdr.detectChanges();
          }
        },
        error: (err) => {
          this.snackBar.open(
            err.error.message || "Error al actualizar el detalle.",
            "Cerrar",
            { duration: 3000 }
          );
        },
      });
  }

  /**
   * Calculate total for payment methods
   * @param payments - Array of payments
   * @param method - Method to sum ('paying' or 'overpayment')
   * @returns Total amount
   */
  calculateTotalMethod(
    payments: any[],
    method: "paying" | "overpayment"
  ): number {
    if (!payments || !Array.isArray(payments)) return 0;

    return payments.reduce((total, payment) => {
      const amount = Number(payment[method]) || 0;
      return total + amount;
    }, 0);
  }

  /**
   * Round number to X decimal places
   * @param num - Number to round
   * @param decimals - Number of decimal places
   * @returns Rounded number
   */
  private roundToX(num: number, decimals: number): number {
    return Math.round(num * Math.pow(10, decimals)) / Math.pow(10, decimals);
  }

  /**
   * Group payments by payment type, bank, and account
   * @param payments - Array of payments to group
   * @returns Grouped payments
   */
  private groupPayments(payments: any[]): any[] {
    const grouped = {};

    payments.forEach((payment) => {
      let key = payment.paymentType?.code || "unknown";
      let groupKey = key;

      const hasBankDetails = payment.bank && payment.account;

      if (hasBankDetails) {
        groupKey = `${key}_${payment.bank.code}_${payment.account.accountNumber}`;
      }

      if (!grouped[groupKey]) {
        grouped[groupKey] = {
          paymentType: payment.paymentType,
          ...(hasBankDetails && {
            bank: payment.bank,
            account: payment.account,
          }),
          totalOverPayment: 0,
          totalPaying: 0,
          originalPayments: [],
        };
      }
      grouped[groupKey].totalOverPayment += payment.overpayment || 0;
      grouped[groupKey].totalPaying += payment.paying || 0;
      grouped[groupKey].originalPayments.push(payment);
    });

    return Object.values(grouped);
  }

  validateJoinOrderModal(ordersToJoinFiltered: OrderAttributes[] | any) {
    if (this.selection.selected.length == 0) {
      return;
    }

    const allCanUnify = ordersToJoinFiltered.every(
      (order) => order.canUnifyConditions
    );
    const uniqueClass = new Set(
      ordersToJoinFiltered.map((order) => order.class?._id || "sin_class")
    );
    const materialMap = new Map<string, Set<number>>();

    ordersToJoinFiltered.forEach((order) => {
      order.detail.forEach((item) => {
        const materialId = item.material?._id || ("sin_item" as any);
        const unitPrice = item.unitPrice;

        if (!materialMap.has(materialId)) {
          materialMap.set(materialId, new Set());
        }
        materialMap.get(materialId)?.add(unitPrice);
      });
    });

    let message = "";
    let hasPriceM = false;

    materialMap.forEach((prices, materialId) => {
      if (prices.size > 1) {
        hasPriceM = true;
      }
    });

    if (!allCanUnify) {
      if (uniqueClass.size > 1 && hasPriceM) {
        message =
          "Las órdenes seleccionadas pertenecen a diferentes clases y tienen variaciones en los precios de los materiales.";
        this.openConfirmSDialog(message);
      } else if (uniqueClass.size > 1) {
        message = "Las órdenes seleccionadas pertenecen a diferentes clases.";
        this.openConfirmSDialog(message);
      } else if (hasPriceM) {
        message =
          "Las órdenes seleccionadas tienen variaciones en los precios de los materiales.";
        this.openConfirmSDialog(message);
      }
    }
    this.joinOrders(ordersToJoinFiltered);
  }

  joinOrders(ordersToJoinFiltered: OrderAttributes[]): void {
    let send: IUpdateOrderUnified = {
      orderIds: ordersToJoinFiltered.map((order) => String(order._id)),
    };

    this.loadService.joinOrder(send).subscribe({
      next: () => {
        this.snackBar.open("Registro actualizado con éxito", "Cerrar", {
          duration: 3000,
        });
        if (this.selection.selected.length > 0) this.clearSelection();
        this.refreshMonitor.emit();
      },
      error: (err) => {
        this.snackBar.open(
          err.error.message || "Error al actualizar registro",
          "Cerrar",
          { duration: 3000 }
        );
      },
    });
  }
}
